/* global rwgps */
import React from 'react'
import { connect } from 'react-redux'
import { browserHistory } from 'react-router'
import styles from './CreateEditCollection.scss'
import { errorMessage } from 'lib/styleguide/base.scss'
import { collectionsMessages } from 'features/homepage/Collections/intlMessages'
import { collectionActions } from 'data/resources/collections'
import { collectionItemActions } from 'data/resources/collectionItems'
import { routeSetOverviewActions, routeSetOverviewSelectors } from 'data/resources/routeSetOverviews'
import { Collection } from 'data/records'
import { ConfirmationDialog } from 'common/styleguide/ConfirmationDialog'
import { ImageUpload } from 'common/styleguide/ImageUpload'
import { Checkbox } from 'common/styleguide/Checkbox'
import plusIcon from 'assets/images/goals/plusIcon.svg'
import { fm, Msg, commonNouns, commonVerbs, privacyLevels } from 'lib/localization'
import { missingPhoto } from 'lib/photoUtils'
import { injectIntl, FormattedMessage as T } from 'react-intl'
import { Map } from 'immutable'

export const CREATE_EDIT_COLLECTION_DIALOG = 'CREATE_EDIT_COLLECTION_DIALOG'

const clubVisibilityOptions = intl => [
  { value: 0, title: fm(intl, privacyLevels.clubCollectionPublic) },
  { value: 1, title: fm(intl, privacyLevels.rmOnly) },
  { value: 2, title: fm(intl, privacyLevels.mOnly) }
]

const reduceBlobURL = image => (
  !image || typeof image === 'string'
    ? image
    : image.preview
)

export class _CreateEditCollection extends React.PureComponent {
  constructor (props) {
    super(props)
    const { collection } = props

    this.state = {
      collection: collection || Collection({ title: '', description: '', visibility: rwgps.config.club?.id ? 0 : 1, showMap: true }),
      pendingEdits: Map(),
      savePending: false,
      error: {}
    }
  }

  setPendingEdit (key, value) {
    const { pendingEdits } = this.state
    this.setState({ pendingEdits: pendingEdits.merge({ [key]: value }) })
  }

  onTitleChange = (e) => {
    this.setPendingEdit('title', e.target.value)
  }

  onDescriptionChange = (e) => {
    this.setPendingEdit('description', e.target.value)
  }

  onVisibilityChange = (e) => {
    this.setPendingEdit('visibility', e.target.value)
  }

  onCoverDrop = (files) => {
    if (files && files[0]) {
      this.setPendingEdit('cover', files[0])
    }
  }

  onShowMapChange = (e) => {
    this.setPendingEdit('showMap', e.currentTarget.checked)
  }

  onSubmit = async (e) => {
    e.preventDefault()
    const {
      createCollection,
      updateCollection,
      batchAddCollectionItems,
      loadRouteSetOverview,
      routeSetOverview,
      onRequestClose,
      itemsToAdd,
      itemType
    } = this.props
    const { collection, pendingEdits } = this.state
    let nextCollection = collection.merge(pendingEdits)

    if (!nextCollection.title || nextCollection.title.trim() === '') {
      this.setState({ savePending: false, error: { title: <Msg m={collectionsMessages.noTitleError} /> } })
      return
    } else if (nextCollection.title.length > 80) {
      this.setState({ savePending: false, error: { title: <Msg m={collectionsMessages.tooLongError} /> } })
      return
    }

    this.setState({ savePending: true })
    const params = Collection.serialize(nextCollection, ['title', 'description', 'visibility', 'cover', 'showMap'])
    try {
      const response = await (collection && collection.id ? updateCollection(collection.id, params) : createCollection(params))
      const { id } = response.value.collection
      this.setState({
        pendingEdits: Map(),
        savePending: false
      })

      itemsToAdd && batchAddCollectionItems({
        collectionId: id,
        items: itemsToAdd.map(item => ({ item_type: itemType, item_id: item.id }))
      })

      if (!collection.showMap && nextCollection.showMap) {
        loadRouteSetOverview(routeSetOverview.id)
      }

      browserHistory.push(`/collections/${id}`)
      onRequestClose()
    } catch (r) {
      this.setState({ error: { server: r.details[0].error_info }, savePending: false })
    }
  }

  render () {
    const {
      onRequestClose,
      modalTitle,
      allowVisibilityUpdate,
      intl
    } = this.props
    const {
      collection,
      pendingEdits,
      savePending,
      error
    } = this.state
    const values = collection && collection.merge(pendingEdits)

    return (
      <ConfirmationDialog
        maxWidth='600px'
        onRequestClose={onRequestClose}
        onConfirm={e => this.onSubmit(e)}
        onCancel={onRequestClose}
        confirmText={<Msg capitalized m={commonVerbs.save} />}
        confirmPending={savePending}
        shouldCloseOnOverlayClick={false}
        title={modalTitle}>
        <form onSubmit={e => this.onSubmit(e)}>
          <div>
            <label htmlFor='title-input'><Msg capitalized m={commonNouns.title} /></label>
            <input id='title-input'
              type='text'
              value={values.title}
              onChange={this.onTitleChange} />
            {error.title && <div className={errorMessage}>{error.title}</div>}
          </div>
          <div>
            <label htmlFor='description-input'><Msg capitalized m={commonNouns.overview} /></label>
            <textarea id='description-input'
              rows='2'
              value={values.description}
              onChange={this.onDescriptionChange} />
          </div>
          {allowVisibilityUpdate && <div>
            <label><Msg m={commonNouns.visibility} /></label>
            <select value={values.visibility} onChange={this.onVisibilityChange}>
              {clubVisibilityOptions(intl).map(o => <option key={o.value} value={o.value}>{o.title}</option>)}
            </select>
          </div>}
          <div>
            <label><Msg capitalized m={commonNouns.images} values={{ count: 1 }} /></label>
            <ImageUpload
              className={styles.imageUploadWithNote}
              onDrop={this.onCoverDrop}
              hintTop={<T
                id='collectionModal.dropPhoto'
                defaultMessage='Drop a photo for your cover here'
                description='Placeholder text for an image uploader' />}>
              {values.cover && !missingPhoto(values.cover)
                ? <img className={styles.cover} src={reduceBlobURL(values.cover)} />
                : <img className={styles.plusIcon} height='80' width='80' src={plusIcon} />}
            </ImageUpload>
          </div>
          <div>
            <Checkbox id='showMap-input'
              type='checkbox'
              checked={values.showMap}
              label={<T
                id='collectionModal.showMap'
                defaultMessage='Show map'
                description='A toggle to show a map' />}
              onChange={this.onShowMapChange} />
          </div>
          {error.server && <div className={errorMessage}>{error.server}</div>}
        </form>
      </ConfirmationDialog>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  routeSetOverview: ownProps.collection && routeSetOverviewSelectors.byCollectionId(state, ownProps.collection.id)
})

const mapDispatchToProps = {
  createCollection: collectionActions.create,
  updateCollection: collectionActions.update,
  batchAddCollectionItems: collectionItemActions.batchAdd,
  loadRouteSetOverview: routeSetOverviewActions.read
}

export const CreateEditCollection = injectIntl(connect(
  mapStateToProps,
  mapDispatchToProps
)(_CreateEditCollection))
